import { useEffect, useRef, useCallback, useState } from 'react';

function useVisible(threshold = 0.1) {
  const [visible, setVisible] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const ref = useCallback((node: HTMLDivElement | null) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (node) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setVisible(true);
        },
        { threshold }
      );
      observer.observe(node);
      observerRef.current = observer;
    }
  }, [threshold]);

  return { visible, ref };
}

const sections = [
  {
    id: 'intro',
    title: '引言',
    content: '深圳融慧数科有限公司（以下简称"我们"或"融慧数科"）深知个人信息对您的重要性，并会尽全力保护您的个人信息安全可靠。我们致力于维持您对我们的信任，恪守以下原则，保护您的个人信息：权责一致原则、目的明确原则、选择同意原则、最少够用原则、确保安全原则、主体参与原则、公开透明原则等。同时，我们承诺按业界成熟的安全标准，采取相应的安全保护措施来保护您的个人信息。',
  },
  {
    id: 'collect',
    title: '一、我们如何收集和使用您的个人信息',
    content: '',
    subsections: [
      {
        title: '1.1 您主动提供的信息',
        content: '当您使用我们的预约演示、联系我们等功能填写表单时，我们可能会收集您的姓名、公司名称、职位、手机号码、电子邮箱地址等信息。这些信息仅用于我们与您取得联系、了解您的需求并提供相应的产品或服务咨询。',
      },
      {
        title: '1.2 我们自动收集的信息',
        content: '当您访问我们的网站时，我们可能会自动收集您的IP地址、浏览器类型、操作系统、访问时间、访问页面、来源网站等技术信息。我们使用这些信息来分析网站流量、优化网站性能、改善用户体验，但不会用于识别您的个人身份。',
      },
      {
        title: '1.3 Cookie及类似技术',
        content: '我们使用Cookie及类似技术来提升您的浏览体验。Cookie是存储在您设备上的小型文本文件，用于记录您的偏好设置和浏览行为。您可以通过浏览器设置管理或禁用Cookie，但这可能影响您使用我们网站的某些功能。',
      },
    ],
  },
  {
    id: 'use',
    title: '二、我们如何使用收集的信息',
    content: '',
    subsections: [
      {
        title: '2.1 信息使用目的',
        content: '我们将您的个人信息用于以下目的：（1）响应您的咨询、预约和业务需求；（2）向您发送与我们产品和服务相关的信息；（3）改进我们的产品、服务和网站体验；（4）进行内部分析和统计研究；（5）履行法律法规规定的义务。',
      },
      {
        title: '2.2 信息使用限制',
        content: '我们不会将您的个人信息用于上述目的之外的任何用途，除非获得您的明确同意或法律法规另有规定。',
      },
    ],
  },
  {
    id: 'share',
    title: '三、我们如何共享、转让、公开披露您的个人信息',
    content: '',
    subsections: [
      {
        title: '3.1 共享',
        content: '我们不会与任何第三方共享您的个人信息，但以下情况除外：（1）获得您的明确同意；（2）与我们的关联公司在必要的范围内共享，且关联公司受本隐私政策的约束；（3）与为我们提供服务的合作伙伴共享，如网站托管服务商、数据分析服务商等，我们会要求他们按照我们的指示处理数据并遵守保密义务；（4）法律法规要求的其他情形。',
      },
      {
        title: '3.2 转让',
        content: '我们不会将您的个人信息转让给任何公司、组织和个人，但以下情况除外：（1）获得您的明确同意；（2）在涉及合并、收购或破产清算时，如涉及个人信息转让，我们会要求新的持有您个人信息的公司、组织继续受本隐私政策的约束，否则我们将要求该公司、组织重新征求您的同意。',
      },
      {
        title: '3.3 公开披露',
        content: '我们仅会在以下情况下公开披露您的个人信息：（1）获得您的明确同意；（2）基于法律法规的要求、法律程序或政府机关的强制性要求。',
      },
    ],
  },
  {
    id: 'security',
    title: '四、我们如何保护您的个人信息',
    content: '',
    subsections: [
      {
        title: '4.1 安全措施',
        content: '我们已采取符合业界标准的安全防护措施保护您提供的个人信息，防止数据遭到未经授权的访问、公开披露、使用、修改、损坏或丢失。我们将尽力确保您的个人信息得到安全保护。',
      },
      {
        title: '4.2 数据保存',
        content: '我们仅在实现本隐私政策所述目的所需的期限内保留您的个人信息，除非法律法规有强制的留存要求。在超出保留期限后，我们会删除或匿名化处理您的个人信息。',
      },
      {
        title: '4.3 安全事件响应',
        content: '若发生个人信息安全事件，我们将按照法律法规的要求，及时向您告知安全事件的基本情况和可能的影响、我们已采取或将要采取的处置措施、您可自主防范和降低风险的建议等。我们将及时以推送通知、邮件等方式告知您。',
      },
    ],
  },
  {
    id: 'rights',
    title: '五、您的权利',
    content: '根据相关法律法规，您享有以下与您的个人信息相关的权利：（1）查阅和复制您的个人信息；（2）更正不准确的个人信息；（3）删除您的个人信息（在法定情形下）；（4）撤回同意；（5）注销账户（如适用）；（6）获取个人信息副本。如果您希望行使上述任何权利，请通过本隐私政策第九条提供的联系方式与我们联系。我们将在收到您的请求后尽快响应，通常不超过15个工作日。',
  },
  {
    id: 'minors',
    title: '六、未成年人保护',
    content: '我们的网站、产品和服务主要面向企业用户，不针对未满18周岁的未成年人。我们不会主动收集未成年人的个人信息。如果您是未成年人，请在父母或监护人的陪同下使用我们的服务。如果我们发现自己在未获得可证实的父母同意的情况下收集了未成年人的个人信息，我们会设法尽快删除相关数据。',
  },
  {
    id: 'updates',
    title: '七、隐私政策的更新',
    content: '我们可能适时修订本隐私政策。未经您明确同意，我们不会削减您按照本隐私政策所应享有的权利。我们会在本页面发布对本隐私政策所做的任何变更，并通过网站公告或其他适当方式提醒您阅读更新内容。对于重大变更，我们还会提供更加显著的通知。本隐私政策所指的重大变更包括但不限于：（1）我们的服务模式发生重大变化，如处理个人信息的目的、处理的个人信息类型、个人信息的使用方式等；（2）我们在所有权结构、组织架构等方面发生重大变化，如业务调整、破产并购等引起的所有者变更等。',
  },
  {
    id: 'contact',
    title: '八、联系我们',
    content: '',
    subsections: [
      {
        title: '',
        content: '如果您对本隐私政策有任何疑问、意见或建议，或希望行使您与个人信息相关的权利，请通过以下方式联系我们：',
      },
      {
        title: '',
        content: '',
        list: [
          '公司名称：深圳融慧数科有限公司',
          '联系地址：深圳市宝安区西乡街道劳动社区前海科兴科学园5号楼702',
          '联系人：柳雪琴',
          '联系电话：18688924109',
          '电子邮箱：liuxueqin@ronghuidt.com',
        ],
      },
      {
        title: '',
        content: '我们将在收到您的反馈后尽快处理，并在15个工作日内回复您的合理请求。',
      },
    ],
  },
  {
    id: 'effective',
    title: '九、生效日期',
    content: '本隐私政策自2025年1月1日起生效，并取代此前所有版本。',
  },
];

export default function Privacy() {
  const headerRef = useVisible(0.3);
  const bodyRef = useVisible(0.05);

  return (
    <main>
      {/* Page Header */}
      <section className="relative pt-24 md:pt-32 pb-12 md:pb-16 bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.3) 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        />
        <div
          className="absolute -top-20 -right-20 w-[500px] h-[500px] rounded-full opacity-[0.2] pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(0,174,239,0.4) 0%, transparent 70%)' }}
        />
        <div
          className="absolute -bottom-32 -left-32 w-[400px] h-[400px] rounded-full opacity-[0.15] pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(0,174,239,0.3) 0%, transparent 70%)' }}
        />
        <div className="absolute top-24 right-[22%] w-2.5 h-2.5 bg-accent/40 rounded-full animate-pulse pointer-events-none" />
        <div className="absolute bottom-24 left-[15%] w-2 h-2 bg-white/30 rounded-full animate-pulse pointer-events-none" style={{ animationDelay: '0.8s' }} />
        <div className="absolute top-40 left-[35%] w-1.5 h-1.5 bg-accent/30 rounded-full animate-pulse pointer-events-none" style={{ animationDelay: '1.6s' }} />
        <div className="absolute top-32 right-[40%] w-1 h-1 bg-white/20 rounded-full animate-pulse pointer-events-none" style={{ animationDelay: '2.2s' }} />
        <div className="absolute top-[15%] right-[5%] w-px h-[300px] bg-gradient-to-b from-transparent via-accent/15 to-transparent pointer-events-none" />

        <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-3xl">
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              隐私政策
            </h1>
            <p className="text-sm md:text-base text-white/60 leading-relaxed">
              深圳融慧数科有限公司重视您的隐私，我们承诺以透明的方式保护您的个人信息安全
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section
        ref={(el) => { bodyRef.ref(el); }}
        className="py-16 md:py-24 bg-white"
      >
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-3xl mx-auto">
            <div
              className={`space-y-12 transition-all duration-500 ${
                bodyRef.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
            >
              {sections.map((section, idx) => (
                <div key={section.id} id={section.id}>
                  <h2 className="text-lg md:text-xl font-bold text-primary mb-4">
                    {section.title}
                  </h2>
                  {section.content && (
                    <p className="text-sm md:text-base text-text-secondary leading-relaxed mb-4">
                      {section.content}
                    </p>
                  )}
                  {section.subsections && section.subsections.map((sub, subIdx) => (
                    <div key={subIdx} className={subIdx > 0 ? 'mt-4' : ''}>
                      {sub.title && (
                        <h3 className="text-sm md:text-base font-semibold text-primary mb-2">
                          {sub.title}
                        </h3>
                      )}
                      {sub.content && (
                        <p className="text-sm md:text-base text-text-secondary leading-relaxed">
                          {sub.content}
                        </p>
                      )}
                      {sub.list && (
                        <ul className="space-y-1.5 mt-2">
                          {sub.list.map((item, itemIdx) => (
                            <li key={itemIdx} className="flex items-start gap-2 text-sm md:text-base text-text-secondary leading-relaxed">
                              <i className="ri-circle-fill text-[6px] text-accent mt-2 shrink-0" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                  {idx < sections.length - 1 && (
                    <hr className="mt-10 border-gray-100" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}